import { type PropsWithChildren, useEffect, useMemo, useState } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import {
  useSalesV4Context,
  useTableRowFilters,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { SALES_METRICS_REPORT_V4 } from "@gql/sales-page-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetSalesMetricsReportV4,
  type GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { formatting, type SalesMetricRow } from "@north-beam/nb-common";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { TooltipTable } from "../tooltip-table";

import { RevenuePieChart } from "./revenue-pie-chart";

interface Props {
  row: SalesMetricRow;
  tableState: TableState;
  rowItemName: string;
  metric: SalesMetricV2;
  tooltipMetrics: {
    metricBlended: SalesMetricV2;
    metricFt: SalesMetricV2;
    metricRtn: SalesMetricV2;
  };
}

export const Tooltip = ({
  row,
  tableState,
  rowItemName,
  metric,
  tooltipMetrics,
  children,
}: PropsWithChildren<Props>) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <GenericTooltip
      noInfoCircle
      overlayClassName={clsx(
        "p-0 rounded-lg border-none shadow-[0_4px_8px] shadow-v2-black/15%",
      )}
      arrowClassName="bg-v2-primary"
      placement="top"
      mouseEnterDelay={500}
      content={
        <Content
          row={row}
          tableState={tableState}
          rowItemName={rowItemName}
          metric={metric}
          tooltipMetrics={tooltipMetrics}
          onLoadingChange={setIsLoading}
        />
      }
    >
      <div
        className={clsx({
          "cursor-wait": isLoading,
          "cursor-default": !isLoading,
        })}
      >
        {children}
      </div>
    </GenericTooltip>
  );
};

interface ContentProps extends Props {
  onLoadingChange: (val: boolean) => void;
}

const Content = ({
  row,
  tableState,
  rowItemName,
  metric,
  tooltipMetrics,
  onLoadingChange,
}: ContentProps) => {
  const { user } = useUser();
  const { isDemoMode } = usePrefs();
  const { designElementManager } = useDesignElementManagerV2();
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval } = useSalesV4Context();
  const { addQueryMetadata } = useQueryMetadata();

  const { metricBlended, metricFt, metricRtn } = tooltipMetrics;

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    advancedSearch,
    isSummary,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const { data, loading } = useNorthbeamQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4, {
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds: [metricBlended.id, metricFt.id, metricRtn.id],
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Revenue",
        topic: "Revenue Tooltip",
        metadata: data.me.salesMetricsReportV4.queryMetadata,
      });
    },
  });

  const { data: dataCash, loading: loadingCash } = useNorthbeamQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4, {
    skip: accountingMode === "cash",
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode: "cash",
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds: [metric.id],
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary: true,
    },
  });

  const result = useMemo(() => {
    if (!data) {
      return { valueBlended: 0, valueFt: 0, valueRtn: 0 };
    }

    const row = data.me.salesMetricsReportV4.actual[0] as SalesMetricRow;

    return {
      valueBlended: metricBlended.getValue(row.metrics) ?? 0,
      valueFt: metricFt.getValue(row.metrics) ?? 0,
      valueRtn: metricRtn.getValue(row.metrics) ?? 0,
    };
  }, [data, metricBlended, metricFt, metricRtn]);

  const tableData = useMemo(() => {
    if (!data || !dataCash) {
      return [];
    }

    const rowAccrual = data.me.salesMetricsReportV4.actual[0] as SalesMetricRow;
    const rowCash = dataCash.me.salesMetricsReportV4
      .actual[0] as SalesMetricRow;

    const getFormattedValue = (value: number) => {
      return formatting.formatMetric(value, metric.format, {
        precision: "exact",
        code: user.currencies.currentCurrency,
      });
    };

    const cashValue = metric.getValue(rowCash.metrics) ?? 0;
    const accrualValue = metric.getValue(rowAccrual.metrics) ?? 0;
    const diffValue = cashValue - accrualValue;

    return [
      {
        label: `Cash ${metric.name}`,
        labelTooltip: `Cash: Total collected ${metric.name}.`,
        value: getFormattedValue(cashValue),
        valueDetails: formatting.formatPercentage(1, {}),
      },
      {
        label: `Accrual (${attributionWindow}D)`,
        labelTooltip: `Accrual (${attributionWindow}D): ${metric.name} attributed to touchpoints that happen within ${attributionWindow} day of the purchase. This captures the customer journeys that fall within your current attribution window.`,
        value: getFormattedValue(accrualValue),
        valueDetails: formatting.formatPercentage(accrualValue / cashValue, {}),
      },
      {
        label: `Accrual (Post-${attributionWindow}D)`,
        labelTooltip: `Accrual (Post-${attributionWindow}D): ${metric.name} attributed to touchpoints that happen more than ${attributionWindow} day before the purchase. This captures customer journeys that are outside of your current attribution window.`,
        value: getFormattedValue(diffValue),
        valueDetails: formatting.formatPercentage(diffValue / cashValue, {}),
      },
    ];
  }, [
    data,
    dataCash,
    metric,
    attributionWindow,
    user.currencies.currentCurrency,
  ]);

  useEffect(() => {
    onLoadingChange(loading || loadingCash);
  }, [onLoadingChange, loading, loadingCash]);

  if (loading || loadingCash) {
    return (
      <div
        className={clsx(
          "p-4 bg-v2-secondary rounded-lg flex items-center justify-center",
          "w-[270px] h-[340px]",
        )}
      >
        <Spinner className="text-v2-cosmic/400" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-v2-secondary rounded-lg text-v2-primary">
      <div className="mb-2">
        <div className="text-sm font-semibold mb-1.5">{metricBlended.name}</div>
      </div>
      {result.valueBlended !== 0 && (
        <div>
          <div className="text-xs inline-flex gap-1 mb-2">
            <span>
              {formatting.formatPercentage(
                result.valueFt / result.valueBlended,
                {
                  mantissa: 0,
                },
              )}{" "}
              of
            </span>
            <span className="font-semibold truncate max-w-[120px]">
              {isDemoMode &&
              tableState.selectedTab !== "breakdown" &&
              rowItemName !== "Grand Total"
                ? generateAnonBase64Hash({
                    value: rowItemName,
                    prefix: tableState.selectedTab,
                    doAnon: isDemoMode,
                  })
                : rowItemName}
            </span>
            <span>{metricBlended.name} is New</span>
          </div>
          <div className="flex items-center justify-center p-4">
            <RevenuePieChart data={result} />
          </div>
          <div className="flex items-center justify-center gap-3 text-2xs text-v2-secondary">
            <div className="flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: designElementManager.getColorForIndex(0),
                }}
              ></div>
              New
            </div>
            <div className="flex items-center gap-1">
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: designElementManager.getColorForIndex(1),
                }}
              ></div>
              Returning
            </div>
          </div>
        </div>
      )}
      {tableData.length > 0 && (
        <div className="mt-4">
          <div className="text-2xs font-semibold mb-3">{metric.name} Split</div>
          <TooltipTable rows={tableData} />
        </div>
      )}
    </div>
  );
};
